import { PortalErrorNotice } from '@/components/common/portal-error-notice';
import { AlertCircle, ArrowLeft, BookOpen, Languages, Search } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { LanguageToggle } from '@/components/common/language-toggle';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { HelpMarkdown } from './help-markdown';
import {
  filterHelpArticles,
  loadHelpArticles,
  loadHelpTranslationStatus,
  selectHelpArticle,
  type HelpArticle,
  type HelpTranslationStatus,
} from '../lib/help-content';
import { searchHelp } from '../lib/help-search';
import { OPEN_HELP_EVENT } from './inline-help';

const EMPTY_PERMISSIONS: readonly string[] = [];
const ALL_FEATURE_PATHS_AVAILABLE = (): boolean => true;

function linkState(): { readonly articleId: string | null; readonly anchor: string | null } {
  const query = new URLSearchParams(window.location.search);
  return { articleId: query.get('help'), anchor: query.get('helpAnchor') };
}

function replaceLink(articleId: string | null, anchor: string | null): void {
  const url = new URL(window.location.href);
  if (articleId === null) url.searchParams.delete('help');
  else url.searchParams.set('help', articleId);
  if (anchor === null) url.searchParams.delete('helpAnchor');
  else url.searchParams.set('helpAnchor', anchor);
  window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
}

export interface HelpPanelProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly pathname: string;
  readonly customerMode?: boolean;
  readonly customerLocale?: string | undefined;
  readonly tenantLocale?: string | undefined;
  readonly permissions?: readonly string[] | undefined;
  readonly isSuperAdmin?: boolean | undefined;
  readonly isFeaturePathAvailable?: ((path: string) => boolean) | undefined;
}

export function HelpPanel({
  open,
  onOpenChange,
  pathname,
  customerMode = false,
  customerLocale,
  tenantLocale,
  permissions = EMPTY_PERMISSIONS,
  isSuperAdmin = false,
  isFeaturePathAvailable = ALL_FEATURE_PATHS_AVAILABLE,
}: HelpPanelProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const selectedLocale = customerLocale ?? i18n.resolvedLanguage ?? i18n.language;
  const [articles, setArticles] = useState<readonly HelpArticle[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(() => linkState().articleId);
  const [anchor, setAnchor] = useState<string | null>(() => linkState().anchor);
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState<'LOADING' | 'READY' | 'ERROR'>('LOADING');
  const [translationStatus, setTranslationStatus] = useState<HelpTranslationStatus | null>(null);
  const [generation, setGeneration] = useState(0);

  useEffect(() => {
    const synchronize = (): void => {
      const state = linkState();
      setSelectedId(state.articleId);
      setAnchor(state.anchor);
    };
    window.addEventListener(OPEN_HELP_EVENT, synchronize);
    window.addEventListener('popstate', synchronize);
    return () => {
      window.removeEventListener(OPEN_HELP_EVENT, synchronize);
      window.removeEventListener('popstate', synchronize);
    };
  }, []);

  useEffect(() => {
    let current = true;
    setStatus('LOADING');
    void loadHelpArticles(selectedLocale, tenantLocale ?? selectedLocale, true)
      .then(async (loaded) => {
        if (!current) return;
        const visible = filterHelpArticles(loaded, {
          customerMode,
          isSuperAdmin,
          permissions,
        }).filter((article) => isSuperAdmin || isFeaturePathAvailable(article.metadata.route));
        const nextTranslationStatus = isSuperAdmin
          ? await loadHelpTranslationStatus(visible)
          : null;
        if (!current) return;
        setArticles(visible);
        setTranslationStatus(nextTranslationStatus);
        setStatus('READY');
      })
      .catch(() => {
        if (current) {
          setArticles([]);
          setTranslationStatus(null);
          setStatus('ERROR');
        }
      });
    return () => {
      current = false;
    };
  }, [
    customerMode,
    generation,
    isFeaturePathAvailable,
    isSuperAdmin,
    permissions,
    selectedLocale,
    tenantLocale,
  ]);

  const selected = useMemo(
    () => selectHelpArticle(articles, selectedId, pathname),
    [articles, pathname, selectedId],
  );
  const results = useMemo(
    () => searchHelp(articles, query, selectedLocale),
    [articles, query, selectedLocale],
  );
  const groupedArticles = useMemo(() => {
    const groups = new Map<string, HelpArticle[]>();
    for (const article of articles) {
      const group = groups.get(article.category) ?? [];
      group.push(article);
      groups.set(article.category, group);
    }
    const collator = new Intl.Collator(selectedLocale);
    return [...groups.entries()]
      .map(([category, entries]) => ({
        category,
        entries: entries.sort((left, right) =>
          collator.compare(left.metadata.title, right.metadata.title),
        ),
      }))
      .sort((left, right) =>
        collator.compare(
          t(`help.categories.${left.category}`),
          t(`help.categories.${right.category}`),
        ),
      );
  }, [articles, selectedLocale, t]);

  useEffect(() => {
    if (!open || selected === undefined) return;
    if (selectedId !== selected.metadata.id) setSelectedId(selected.metadata.id);
    replaceLink(selected.metadata.id, anchor);
  }, [anchor, open, selected, selectedId]);

  useEffect(() => {
    if (!open || anchor === null) return;
    requestAnimationFrame(() =>
      document.getElementById(anchor)?.scrollIntoView({ block: 'start' }),
    );
  }, [anchor, open, selectedLocale, selected?.metadata.id]);

  const choose = (article: HelpArticle): void => {
    setSelectedId(article.metadata.id);
    setAnchor('overview');
    setQuery('');
  };

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        if (!next) replaceLink(null, null);
        onOpenChange(next);
      }}
    >
      <SheetContent
        closeLabel={t('common.close')}
        className="w-full gap-4 p-4 sm:max-w-2xl sm:p-6"
        dir={document.documentElement.dir}
      >
        <SheetHeader className="pe-10">
          <SheetTitle>{t('help.title')}</SheetTitle>
          <SheetDescription>{selected?.metadata.description ?? t('help.loading')}</SheetDescription>
          {customerMode ? null : (
            <div className="flex flex-wrap items-center gap-3 pt-2">
              <LanguageToggle />
              {translationStatus === null ? null : (
                <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                  <Languages className="h-3.5 w-3.5" aria-hidden="true" />
                  {t('help.translationStatus', {
                    current: translationStatus.currentCount,
                    total: translationStatus.sourceCount,
                    locales: translationStatus.localeCount,
                    fallback: translationStatus.fallbackCount,
                    stale: translationStatus.staleCount,
                  })}
                </span>
              )}
            </div>
          )}
        </SheetHeader>
        <div className="relative">
          <Search
            className="pointer-events-none absolute start-3 top-3 h-4 w-4 text-muted-foreground"
            aria-hidden="true"
          />
          <Input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t('help.search')}
            aria-label={t('help.search')}
            className="ps-9"
          />
        </div>
        {status === 'LOADING' ? (
          <p className="text-sm text-muted-foreground" aria-live="polite">
            {t('help.loading')}
          </p>
        ) : null}
        {status === 'ERROR' && pathname.startsWith('/portal') ? (
          <PortalErrorNotice
            error={`help:${generation}`}
            kind="server"
            retry={() => setGeneration((value) => value + 1)}
          />
        ) : status === 'ERROR' ? (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {t('help.error')}{' '}
              <Button
                variant="link"
                className="h-auto p-0"
                onClick={() => setGeneration((value) => value + 1)}
              >
                {t('help.retry')}
              </Button>
            </AlertDescription>
          </Alert>
        ) : null}
        {status === 'READY' && articles.length === 0 ? (
          <Alert>
            <BookOpen className="h-4 w-4" />
            <AlertDescription>
              {t('help.empty')}{' '}
              <Button
                variant="link"
                className="h-auto p-0"
                onClick={() => setGeneration((value) => value + 1)}
              >
                {t('help.retry')}
              </Button>
            </AlertDescription>
          </Alert>
        ) : null}
        {query.length > 0 ? (
          <nav
            aria-label={t('help.allTopics')}
            className="max-h-64 overflow-y-auto rounded-md border p-2"
          >
            {results.length === 0 ? (
              <p className="p-2 text-sm text-muted-foreground">{t('help.noResults')}</p>
            ) : (
              results.map((article) => (
                <button
                  key={article.metadata.id}
                  type="button"
                  className="block min-h-11 w-full rounded-md px-3 py-2 text-start hover:bg-accent"
                  onClick={() => choose(article)}
                >
                  <span className="block text-sm font-medium">{article.metadata.title}</span>
                  <span className="block text-xs text-muted-foreground">
                    {article.metadata.description}
                  </span>
                </button>
              ))
            )}
          </nav>
        ) : null}
        {selected === undefined ? null : (
          <div className="grid min-h-0 flex-1 gap-4 sm:grid-cols-[14rem_minmax(0,1fr)]">
            {query.length > 0 ? null : (
              <nav
                aria-label={t('help.allTopics')}
                className="max-h-48 overflow-y-auto rounded-md border border-border p-2 sm:max-h-none"
              >
                {groupedArticles.map((group) => (
                  <section key={group.category} className="mb-3 last:mb-0">
                    <h2 className="px-2 pb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      {t(`help.categories.${group.category}`)}
                    </h2>
                    {group.entries.map((article) => (
                      <button
                        key={article.metadata.id}
                        type="button"
                        aria-current={
                          article.metadata.id === selected.metadata.id ? 'page' : undefined
                        }
                        className="block min-h-11 w-full rounded-md px-2 py-2 text-start text-sm hover:bg-accent aria-[current=page]:bg-accent aria-[current=page]:font-medium"
                        onClick={() => choose(article)}
                      >
                        {article.metadata.title}
                      </button>
                    ))}
                  </section>
                ))}
              </nav>
            )}
            <div
              className="min-h-0 overflow-y-auto overscroll-contain pe-1"
              lang={selected.resolvedLocale}
            >
              {selected.usedFallback ? (
                <Alert className="mb-4">
                  <AlertDescription>{t('help.fallback')}</AlertDescription>
                </Alert>
              ) : null}
              <HelpMarkdown body={selected.body} />
              {selected.metadata.related.length > 0 ? (
                <nav className="mt-8 border-t pt-4" aria-label={t('help.related')}>
                  <h2 className="mb-2 text-sm font-semibold">{t('help.related')}</h2>
                  {selected.metadata.related.map((id) => {
                    const article = articles.find((candidate) => candidate.metadata.id === id);
                    return article === undefined ? null : (
                      <Button
                        key={id}
                        variant="link"
                        className="h-auto min-h-11 px-0 text-start"
                        onClick={() => choose(article)}
                      >
                        <ArrowLeft className="h-4 w-4 rtl:rotate-180" aria-hidden="true" />
                        {article.metadata.title}
                      </Button>
                    );
                  })}
                </nav>
              ) : null}
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
